export interface FieldError {
  field: string;
  code: string;
  message: string;
}

export interface ProblemDetails {
  type?: string;
  title: string;
  status: number;
  code: string;
  detail?: string;
  traceId?: string;
  fieldErrors?: FieldError[];
}

export class ApiError extends Error {
  constructor(public readonly problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
  }
}

let csrfToken: string | undefined;

export async function bootstrapCsrf(): Promise<string> {
  if (csrfToken) return csrfToken;
  const response = await fetch("/api/v1/auth/csrf", {
    credentials: "same-origin",
  });
  if (!response.ok) throw await toApiError(response);
  const data = (await response.json()) as { csrfToken: string };
  csrfToken = data.csrfToken;
  return data.csrfToken;
}

async function toApiError(response: Response): Promise<ApiError> {
  const fallback: ProblemDetails = {
    title: "Request failed",
    status: response.status,
    code: "HTTP_ERROR",
  };
  try {
    const contentType = response.headers.get("content-type") ?? "";
    if (contentType.includes("application/problem+json")) {
      return new ApiError((await response.json()) as ProblemDetails);
    }
  } catch {
    // Do not expose malformed server bodies.
  }
  return new ApiError(fallback);
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json, application/problem+json");
  if (init.body && !(init.body instanceof FormData))
    headers.set("Content-Type", "application/json");
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    headers.set("X-CSRF-Token", await bootstrapCsrf());
  }
  const response = await fetch(`/api/v1${path}`, {
    ...init,
    method,
    headers,
    credentials: "same-origin",
  });
  if (!response.ok) {
    if (response.status === 401)
      window.dispatchEvent(new CustomEvent("icat:unauthorized"));
    throw await toApiError(response);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export interface ApiDownload {
  blob: Blob;
  filename: string;
}

export async function apiDownload(
  path: string,
  init: RequestInit = {},
): Promise<ApiDownload> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  headers.set(
    "Accept",
    "application/octet-stream, text/csv, application/problem+json",
  );
  if (init.body && !(init.body instanceof FormData))
    headers.set("Content-Type", "application/json");
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    headers.set("X-CSRF-Token", await bootstrapCsrf());
  }
  const response = await fetch("/api/v1" + path, {
    ...init,
    method,
    headers,
    credentials: "same-origin",
  });
  if (!response.ok) {
    if (response.status === 401)
      window.dispatchEvent(new CustomEvent("icat:unauthorized"));
    throw await toApiError(response);
  }
  const disposition = response.headers.get("content-disposition") ?? "";
  const filename =
    /filename="([^"]+)"/.exec(disposition)?.[1] ?? "download";
  return { blob: await response.blob(), filename };
}
